/**
 * reviewLegalDocument — RPC contract for the AI statutory-compliance review
 * of an operator's own legal document.
 *
 * Cross-process contract between the Settings → Legal editor (a
 * `web/src/legal/` callable wrapper) and
 * `firebase/src/legal/reviewLegalDocument/reviewLegalDocument.webhook.ts`
 * (onCall handler, wrapping `reviewLegalDocument.chat.ts`'s one `chat()`
 * seam). Both ends parse with `.strict()` — an extra key fails the whole
 *
 * There is no heuristic layer here and no marker list: the model reads the
 * whole document and judges substance, the same way a lawyer would. Matching
 * a citation string cannot do this job in either direction. It rejects
 * compliant text — `§ 36 Abs. 1 VSBG`, `§§ 36, 37 VSBG`, the non-breaking
 * space a Word paste produces, or counsel's own wording with no § cited at
 * all — and it accepts text that says the opposite of the clause, since
 * "Nach § 312g Abs. 1 BGB steht dem Käufer ein Widerrufsrecht zu" carries the
 * very citation that is supposed to prove the right is EXCLUDED. Whether a
 * clause excludes or grants requires reading it.
 *
 * THE CALLER SENDS THE DOCUMENT AS COMPOSED FOR READERS, never an operator's
 * raw stored source text. The two differ for a privacy document: the
 * platform-maintained recipients/processors section is not something the
 * operator's text ever contained — `resolveLegalDocument` (`@momo/i18n`)
 * composes it in at render, `buildPrivacy` carries it in place for the
 * platform's own default, and the editor's own preview composes it the same
 * way — and its recipient list is filtered by what this deployment has
 * actually switched on (`deploymentProcessors`), so wallet and maps entries
 * are absent until their capability is live. A caller that sends raw source
 * text instead is asking the model to review a document missing a section
 * every reader receives, and every single review comes back with a phantom
 * "no recipients disclosure" finding: correct about the string it was handed,
 * wrong about anything a reader ever sees, and the worst possible first
 * impression for a surface whose whole value is that its findings are real.
 *
 * Fields justified:
 *   kind           — which document this is (terms/privacy), so the model
 *                     reviews it against that document's own statutory
 *                     ground — @momo/i18n's shared `LegalDocumentKindSchema`,
 *                     never a second enum.
 *   document.de/en — the reader-composed text in both locales this platform
 *                     ships (see the invariant above), together in one call,
 *                     so a clause present in one language and silently
 *                     dropped from the other's own translation is detectable
 *                     — precisely the § 305 Abs. 2 Nr. 2 BGB failure this
 *                     codebase already reasons about elsewhere, and only
 *                     detectable with both texts in hand at once. Bounded to
 *                     the same 65536-char cap `LegalContentSchema` stores
 *                     each locale's text under; composition only grows the
 *                     text, so a source at the cap is bounded here on the
 *                     composed value rather than the operator's own.
 *   operatorLocale — the language every finding's explanation renders in,
 *                     so the operator reads their own review in the
 *                     language they work in, not the document's.
 *
 * Output:
 *   findings — every issue the review found, most consequential first; an
 *              empty array is itself the answer for a document with nothing
 *              wrong — never a sentinel "no issues" finding standing in for
 *              that state.
 *
 * Organization context is explicit in the request. The server validates
 * membership or possession of the scoped access token independently.
 */
import { LegalDocumentKindSchema, LocaleEnum } from "@momo/i18n";
import { z } from "zod";
import { Id } from "./workspace.entity.ts";

/**
 * ReviewSeveritySchema — how urgently an operator, not a lawyer, should act
 * on a finding. `critical` and `warning` are real compliance gaps, ranked by
 * exposure; `advisory` is legally adequate text still worth tightening;
 * `verify` is not a text defect at all — it names an affirmative claim the
 * document makes about the operator's own process, which the model has no
 * way to confirm from the text alone. Each variant's own meaning is defined
 * in `reviewLegalDocument.chat.ts`'s own prompt body, beside the document it
 * judges, per this repo's own glossary-in-prompt convention for a variant
 * set an LLM output drives that isn't self-evident from the token alone.
 */
export const ReviewSeveritySchema = z.enum([
	"critical",
	"warning",
	"advisory",
	"verify",
]);
export type ReviewSeverity = z.infer<typeof ReviewSeveritySchema>;

/**
 * ReviewFindingSchema — one issue the review raised. Field order is
 * evidence → statute (what grounds the finding) → severity (the judged
 * signal) → title/explanation/locales (what the operator is told): the
 * model locates real text, or its absence, before it commits to a severity,
 * rather than the reverse.
 *
 * Fields justified:
 *   evidence    — forces the model to show its work before it rates
 *                 anything; the operator-facing text this schema separately
 *                 carries (title/explanation) is written for a non-lawyer,
 *                 evidence is not.
 *   statute     — nullish: an internal-consistency finding or an
 *                 operator-claim finding may rest on no specific provision.
 *   severity    — see ReviewSeveritySchema above.
 *   title       — a short label; the finding list is itself the outline, so
 *                 an operator scanning ordered titles gets the shape of the
 *                 review before reading a single explanation.
 *   explanation — the operator-facing write-up; carries both what is wrong
 *                 and what it means for them, since a non-lawyer needs the
 *                 consequence stated, not just the defect.
 *   locales     — a finding's own scope: both locales when the issue reads
 *                 the same in each, or only the locale(s) where it actually
 *                 occurs, so the operator knows which language version to
 *                 edit and a clause dropped from one translation reads as
 *                 the single-locale defect it is rather than a verdict on
 *                 the document as a whole.
 */
export const ReviewFindingSchema = z.object({
	evidence: z
		.string()
		.min(1)
		.describe(
			"the document's own passage or passages this finding is grounded in, quoted or closely paraphrased — every passage a contradiction between two clauses turns on — or, when the finding is that something the review covers is missing entirely, a statement that no such passage exists",
		),
	statute: z
		.string()
		.min(1)
		.nullish()
		.describe(
			"the statutory provision this finding rests on, cited in its own form — absent when the finding does not turn on a specific provision",
		),
	severity: ReviewSeveritySchema.describe(
		"how urgently the operator must act on this finding, from the most consequential compliance gap down to a claim only the operator themselves can verify",
	),
	title: z
		.string()
		.min(1)
		.describe("a short label naming the clause or gap this finding concerns"),
	explanation: z
		.string()
		.min(1)
		.describe(
			"what is wrong with the document and what it means for the operator, written for someone without legal training",
		),
	locales: z
		.array(LocaleEnum)
		.min(1)
		.describe(
			"the document's own locale or locales this finding applies to — every locale the document ships when the issue reads the same in each, or only the locale where it actually occurs",
		),
});
export type ReviewFindingOutput = z.infer<typeof ReviewFindingSchema>;

export const ReviewLegalDocumentInput = z
	.object({
		orgId: Id,
		kind: LegalDocumentKindSchema,
		document: z.object({
			de: z
				.string()
				.min(1)
				.max(65536)
				.describe(
					"this document's complete German-language text as a reader receives it, every composed-in section included",
				),
			en: z
				.string()
				.min(1)
				.max(65536)
				.describe(
					"this document's complete English-language text as a reader receives it, every composed-in section included",
				),
		}),
		operatorLocale: LocaleEnum.describe(
			"the UI language the operator reading this review works in — every finding's explanation renders in this language",
		),
	})
	.strict();
export type ReviewLegalDocumentInput = z.infer<typeof ReviewLegalDocumentInput>;

export const ReviewLegalDocumentOutput = z
	.object({
		findings: z
			.array(ReviewFindingSchema)
			.describe(
				"every compliance issue the review found, ordered most consequential first; an empty array means the review found nothing to raise",
			),
	})
	.strict();
export type ReviewLegalDocumentOutput = z.infer<
	typeof ReviewLegalDocumentOutput
>;
